import { tweened } from 'svelte/motion';
import { linear, quadOut, elasticOut, elasticIn, backInOut } from 'svelte/easing';
import { writable } from 'svelte/store';
import type { Writable } from 'svelte/store';
import { encode } from 'blurhash';
import type { FPost, ICategory, ISubCategory } from 'garden-common';

export const itemData = writable<FPost<string>[]>([]);
export const selectedSubCategory = writable<ISubCategory>(null);
export const selectedCategory = writable<ICategory>(null);

export const isOwner = writable<boolean>(false);

class ProgressManager {
  private progressList: { groupId: number; progresses: Map<string, Writable<number>>; }[];

  constructor() {
    this.progressList = [];
  }

  findGroupIndexByFileName(fileName: string): number | undefined {
    const encodedFileName = this.safeFileName(fileName);
    const indexFound = this.progressList.findIndex(entry => entry.progresses.has(encodedFileName));
    return indexFound;
  }

  private safeFileName(fileName: string): string {
    return encodeURIComponent(fileName.trim());
  }

  private async wait(duration: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, duration));
  }

  private async getOrCreateProgressStore(
    encodedFileName: string,
    groupId: number
  ): Promise<Writable<number>> {
    // this.log(`GetOrCreateProgressStore: FileName=${encodedFileName}, GroupId=${groupId}`)
    let groupEntry = this.progressList.find(entry => entry.groupId === groupId);

    if (!groupEntry) {
      groupEntry = { groupId, progresses: new Map() };
      this.progressList.push(groupEntry);
    }

    let progressStore = groupEntry.progresses.get(encodedFileName);

    if (!progressStore) {
      progressStore = tweened(0, { duration: 400, easing: quadOut });
      groupEntry.progresses.set(encodedFileName, progressStore);
    }

    return progressStore;
  }

  async initializeUploadProgressGroup(groupId: number): Promise<void> {
    if (!this.progressList.some(entry => entry.groupId === groupId)) {
      this.progressList.push({ groupId, progresses: new Map() });
    }
  }

  async initializeUploadProgress(fileName: string, groupId: number): Promise<void> {
    const encodedFileName = this.safeFileName(fileName);
    await this.getOrCreateProgressStore(encodedFileName, groupId);
    await this.wait(0);
  }

  async updateUploadProgress(
    fileName: string,
    progress: number,
    groupId: number,
    seekAndUpdate: boolean = false
  ): Promise<void> {
    const encodedFileName = this.safeFileName(fileName);
    let groupEntryIndex = this.progressList.findIndex(entry => entry.groupId === groupId);
    if (seekAndUpdate && groupEntryIndex === -1) {
      // Seek and update using filename search
      const foundGroupIndex = this.findGroupIndexByFileName(fileName);
      if (foundGroupIndex !== undefined) {
        groupEntryIndex = foundGroupIndex;
      }
    }
    const progressStore = await this.getOrCreateProgressStore(encodedFileName, groupId);
    progressStore.set(progress);
  }

  async removeUploadProgress(
    fileName: string,
    groupId: number,
    seekAndDelete: boolean = false
  ): Promise<void> {
    const encodedFileName = this.safeFileName(fileName);

    let groupEntryIndex = this.progressList.findIndex(entry => entry.groupId === groupId);
    if (seekAndDelete && groupEntryIndex === -1) {
      // Seek and delete using filename search
      const foundGroupIndex = this.findGroupIndexByFileName(fileName);
      if (foundGroupIndex !== undefined) {
        groupEntryIndex = foundGroupIndex;
      }
    }

    if (groupEntryIndex !== -1) {
      const progressMap = this.progressList[groupEntryIndex].progresses;
      progressMap.delete(encodedFileName);

      if (progressMap.size === 0) {
        this.progressList.splice(groupEntryIndex, 1);
      }
    }
  }

  async getProgressStore(fileName: string, groupId: number): Promise<Writable<number>> {
    const encodedFileName = this.safeFileName(fileName);
    // this.log(`GetProgressStore: FileName=${encodedFileName}, GroupId=${groupId}`)
    return this.getOrCreateProgressStore(encodedFileName, groupId);
  }

  async setBadTween(fileName: string, groupId: number): Promise<void> {
    const encodedFileName = this.safeFileName(fileName);
    const progressStore = await this.getOrCreateProgressStore(encodedFileName, groupId);
    if (!progressStore) {
      throw new Error('No store found for bad tween set.');
    }
    progressStore.set(-1);
  }
  clearGroupProgress(groupId: number): void {
    const groupIndex = this.progressList.findIndex(entry => entry.groupId === groupId);
    if (groupIndex !== -1) {
      const groupEntry = this.progressList[groupIndex];
      for (const progressStore of groupEntry.progresses.values()) {
        progressStore.set(100);
      }
      this.progressList.splice(groupIndex, 1);
    }
  }
  clearAllProgresses() {
    for (const entry of this.progressList) {
      for (const progressStore of entry.progresses.values()) {
        progressStore.set(100);
      }
    }
    this.progressList = [];
  }
}

export const progressManager = new ProgressManager();

/**
 * BlurhashManager class manages Blurhash encoding and decoding for images.
 */
class BlurhashManager {
  private blurs: Map<string, string>; // Map to store blurhash values with corresponding identifiers
  private readonly maxWidth: number; // Maximum width for resizing images
  private readonly maxHeight: number; // Maximum height for resizing images

  /**
   * Constructor for BlurhashManager class.
   * Initializes the blur map and sets maximum width and height.
   */
  constructor() {
    this.blurs = new Map();
    this.maxWidth = 100;
    this.maxHeight = 100;
  }

  /**
   * Internal method to trim and encode file names.
   * @param fileName - The file name to be encoded.
   * @returns The encoded and trimmed file name.
   */
  private safeFileName(fileName: string): string {
    return encodeURIComponent(fileName.trim());
  }

  /**
   * Asynchronously loads an image from a data URL and scales it while maintaining aspect ratio.
   * @param dataUrl - The data URL of the image.
   * @returns A promise that resolves with a scaled HTMLCanvasElement.
   */
  private async loadImage(dataUrl: string): Promise<HTMLCanvasElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        // Calculate scale to keep aspect ratio
        let scale = Math.min(this.maxWidth / img.width, this.maxHeight / img.height);
        scale = scale > 1 ? 1 : scale;

        // Create canvas to scale the image
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        // Draw the scaled image to the canvas and resolve with the canvas
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas);
        img.onload = null;
      };
      img.onerror = event => {
        console.error('Error loading image:', dataUrl, event);
        reject(event); // Reject the promise with the event
        img.onerror = null;
      };
      img.src = dataUrl;
    });
  }

  async convertVideoToImage(videoDataURL: string) {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      // Add source elements to the video
      const sourceElement = document.createElement('source');
      sourceElement.src = videoDataURL;
      video.appendChild(sourceElement);
      // min height
      video.width = 400;
      video.height = 400;
      let frameCaptured = false;
      video.oncanplaythrough = () => {
        if (!frameCaptured) {
          video.currentTime = video.duration / 2; // seek to the middle of the video
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const imageDataURL = canvas.toDataURL('image/png');
          resolve(imageDataURL);
          frameCaptured = true; // set the flag to true after capturing the frame
        }
      };
      video.onerror = error => {
        reject(error);
      };
    });
  }

  /**
   * Retrieves the ImageData from a given HTMLCanvasElement.
   * @param image - The HTMLCanvasElement containing the image.
   * @returns The ImageData of the image.
   */
  private getImageData(image: HTMLCanvasElement): ImageData {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not find context');
    ctx.drawImage(image, 0, 0);
    return ctx.getImageData(0, 0, image.width, image.height);
  }

  /**
   * Encodes an image to Blurhash.
   * @param dataUrl - The data URL of the image.
   * @returns A promise that resolves with the Blurhash string, or undefined on failure.
   */
  private async encodeImageToBlurhash(dataUrl: string): Promise<string | undefined> {
    try {
      const image = await this.loadImage(dataUrl);
      const imageData = this.getImageData(image);
      return encode(imageData.data, imageData.width, imageData.height, 4, 4);
    } catch (error) {
      console.error('Error encoding image to blurhash:', error);
      return undefined;
    }
  }

  /**
   * Initializes Blurhash for an image and associates it with an identifier.
   * @param dataUrl - The data URL of the image.
   * @param identifier - The identifier for the image.
   * @returns A promise that resolves when initialization is complete.
   */
  public async initializeBlurhash(dataUrl: any, identifier: string): Promise<void> {
    identifier = this.safeFileName(identifier);
    // TODO: TYPING: This can have a better type
    try {
      const blur = await this.encodeImageToBlurhash(dataUrl);
      if (blur) {
        this.blurs.set(identifier, blur);
      }
    } catch (error) {
      console.error(error, identifier);
    }
  }

  /**
   * Removes the Blurhash associated with a given identifier.
   * @param identifier - The identifier for the image.
   */
  public removeBlurhash(identifier: string) {
    identifier = this.safeFileName(identifier);
    this.blurs.delete(identifier);
  }

  /**
   * Retrieves the Blurhash associated with a given identifier.
   * @param identifier - The identifier for the image.
   * @returns The Blurhash string or undefined if not found.
   */
  public getBlurhash(identifier: string): string | undefined {
    identifier = this.safeFileName(identifier);
    return this.blurs.get(identifier);
  }
}

export const blurhashManager = new BlurhashManager();
export { BlurhashManager, ProgressManager };
